/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef, memo, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import CustomGrid from 'components/grids/CustomGrid';
import PropTypes from 'prop-types';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { tabs } from 'components/layouts/Tab';
import { MdIvesMdlEqpLnIspt, MdIvesMdlSmly } from 'models/md/MdIves';
import { DefaultProps } from 'pages/md/invest/MdIvesCmpuRegPage';
import LineInqPopup from '../../../popup/LineInqPopup';
import { findMdIvesLnEqpIsptList, saveMdIvesLnEqpIsptList } from 'apis/md/MdProject';
import { CellType } from '@grapecity/wijmo.grid';
import * as input from '@grapecity/wijmo.input';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';

interface LineEqpNbuIsptProps extends DefaultProps {
  selectedSmlyModel?: MdIvesMdlSmly;
  isInit?: any;
  setIsInit: Dispatch<any>;
}

const LineEqpNbuIspt = ({
  elmPrjId,
  ivesMdlId,
  selectedSmlyModel,
  isInit,
  setIsInit,
  isEditable,
  isRegStat,
}: LineEqpNbuIsptProps) => {
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const gridRef = useRef<any>();
  const originalValueRef = useRef<any>(null); // 라인/설비대수 값 변경 시 원본 값 저장용 ref (렌더링에 상관 없음)
  const [specMappingCodes, setSpecMappingCodes] = useState<Code[]>([]);
  const [rowData, setRowData] = useState<MdIvesMdlEqpLnIspt[]>([]);
  const rollbackData = useRef<MdIvesMdlEqpLnIspt[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [isOpenAddPopup, setIsOpenAddPopup] = useState<boolean>(false);

  const tabMenus = [
    {
      id: 'E',
      name: String(t('md.label.전극', '전극')),
    },
    {
      id: 'C',
      name: String(t('md.label.파우치', '파우치')),
    },
  ];

  const isModify = () => {
    return !!ivesMdlId && !ivesMdlId.includes('_NEW_') && !!elmPrjId;
  };

  const layoutDefinition = useMemo(() => {
    const result = [
      {
        binding: 'bildPlntCdNm',
        header: String(t('com.label.플랜트', '플랜트')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'bldoNm',
        header: String(t('com.label.동 명', '동 명')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'prdnLnNm',
        header: String(t('com.label.생산호기', '생산호기')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },

      // 전극 탭
      {
        header: String(t('com.label.믹서', '믹서')),
        columns: [
          {
            binding: 'MIXCES0590',
            header: String(t('com.label.양극', '양극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
          {
            binding: 'MIXAES0590',
            header: String(t('com.label.음극', '음극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        header: String(t('com.label.코터', '코터')),
        columns: [
          {
            binding: 'COTCES0590',
            header: String(t('com.label.양극', '양극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
          {
            binding: 'COTAES0590',
            header: String(t('com.label.음극', '음극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        header: String(t('com.label.롤프레스', '롤프레스')),
        columns: [
          {
            binding: 'ROLCES0590',
            header: String(t('com.label.양극', '양극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
          {
            binding: 'ROLAES0590',
            header: String(t('com.label.음극', '음극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        header: String(t('com.label.슬리터', '슬리터')),
        columns: [
          {
            binding: 'SLTCES0590',
            header: String(t('com.label.양극', '양극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
          {
            binding: 'SLTAES0590',
            header: String(t('com.label.음극', '음극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 0,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },

      // 셀 탭
      {
        header: String(t('com.label.N&D', 'N&D')),
        columns: [
          {
            binding: 'NNDCES0590',
            header: String(t('com.label.양극', '양극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 1,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
          {
            binding: 'NNDAES0590',
            header: String(t('com.label.음극', '음극')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 1,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        header: String(t('com.label.S&F', 'S&F')),
        columns: [
          {
            header: String(t('com.label.Lami', 'Lami')),
            columns: [
              {
                binding: 'SNF-ES0125',
                header: String(t('com.label.A Type', 'A Type')),
                align: 'center',
                minWidth: 40,
                width: '*',
                visible: currentTabIndex === 1,
                format: 'n0',
                editor: new input.InputNumber(document.createElement('div'), {
                  isRequired: false,
                  format: 'n0',
                }),
              },
              {
                binding: 'SNF-ES0130',
                header: String(t('com.label.B Type', 'B Type')),
                align: 'center',
                minWidth: 40,
                width: '*',
                visible: currentTabIndex === 1,
                format: 'n0',
                editor: new input.InputNumber(document.createElement('div'), {
                  isRequired: false,
                  format: 'n0',
                }),
              },
            ],
          },
          {
            binding: 'SNF-ES0065',
            header: String(t('com.label.Folding', 'Folding')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 1,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        binding: 'LNS-ES0590',
        header: String(t('com.label.L&S', 'L&S')),
        align: 'center',
        minWidth: 40,
        width: '*',
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
      {
        header: String(t('com.label.AZS/DNC', 'AZS/DNC')),
        columns: [
          {
            header: String(t('com.label.Cutter', 'Cutter')),
            columns: [
              {
                binding: 'AZSDNC-ES0030',
                header: String(t('com.label.양극', '양극')),
                align: 'center',
                minWidth: 40,
                width: '*',
                visible: currentTabIndex === 1,
                format: 'n0',
                editor: new input.InputNumber(document.createElement('div'), {
                  isRequired: false,
                  format: 'n0',
                }),
              },
              {
                binding: 'AZSDNC-ES0035',
                header: String(t('com.label.음극', '음극')),
                align: 'center',
                minWidth: 40,
                width: '*',
                visible: currentTabIndex === 1,
                format: 'n0',
                editor: new input.InputNumber(document.createElement('div'), {
                  isRequired: false,
                  format: 'n0',
                }),
              },
            ],
          },
          {
            binding: 'AZSDNC-ES0265',
            header: String(t('com.label.Stacker', 'Stacker')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 1,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        binding: 'PKG-ES0590',
        header: String(t('com.label.PKG', 'PKG')),
        align: 'center',
        minWidth: 40,
        width: '*',
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
      {
        binding: 'HPCD-ES0590',
        header: String(t('com.label.HPCD', 'HPCD')),
        align: 'center',
        minWidth: 40,
        width: '*',
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
      {
        binding: 'JIG-ES0590',
        header: String(t('com.label.Jig Formation', 'Jig Formation')),
        align: 'center',
        minWidth: 40,
        width: 110,
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
      {
        binding: 'DGS-ES0590',
        header: String(t('com.label.DGS', 'DGS')),
        align: 'center',
        minWidth: 40,
        width: '*',
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
      {
        header: String(t('com.label.충방전기', '충방전기')),
        columns: [
          {
            binding: 'FOR-ES0005',
            header: String(t('com.label.BOX', 'BOX')),
            align: 'center',
            minWidth: 40,
            width: '*',
            visible: currentTabIndex === 1,
            format: 'n0',
            editor: new input.InputNumber(document.createElement('div'), {
              isRequired: false,
              format: 'n0',
            }),
          },
        ],
      },
      {
        binding: 'SFEOL-ES0590',
        header: String(t('com.label.EOL', 'EOL')),
        align: 'center',
        minWidth: 40,
        width: '*',
        visible: currentTabIndex === 1,
        format: 'n0',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n0',
        }),
      },
    ];
    return result;
  }, [rowData, currentTabIndex]);

  const getLnEqpData = async () => {
    if (!ivesMdlId || !elmPrjId || ivesMdlId.includes('_NEW_')) {
      setRowData([]);
      rollbackData.current = [];
      return;
    }

    const INSPECTION = await findMdIvesLnEqpIsptList(
      ivesMdlId,
      elmPrjId,
      tabMenus[currentTabIndex].id
    );
    const transData = transformDataForGrid(INSPECTION);
    setRowData(transData);
    rollbackData.current = _.cloneDeep(transData);
    if (gridRef.current) gridRef.current.selectedIndex = -1;
  };

  // list -> pivot 변경 함수
  const transformDataForGrid = (list: MdIvesMdlEqpLnIspt[]): MdIvesMdlEqpLnIspt[] => {
    if (!list || list.length === 0) return [];

    const groupedObj = list.reduce((acc, curr) => {
      // 1. 고유한 그룹핑 복합 키 생성 (플랜트 | 동 | 호기)
      const compositeKey = `${curr.bildPlntCd}|${curr.bldoId}|${curr.prdnLnId}`;

      // 2. 그룹이 처음 등장하면 뼈대(기본 구조) 생성
      if (!acc[compositeKey]) {
        acc[compositeKey] = {
          elmPrjId: curr.elmPrjId,
          ivesMdlId: curr.ivesMdlId,
          bildPlntCd: curr.bildPlntCd,
          bildPlntCdNm: curr.bildPlntCdNm,
          bldoId: curr.bldoId,
          bldoNm: curr.bldoNm,
          prdnLnId: curr.prdnLnId,
          prdnLnNm: curr.prdnLnNm,
          prdnLnTpCd: curr.prdnLnTpCd,
        };
      }

      // 3. optValCtn1 로 키값 생성
      const bindingKey = `${curr.optValCtn1 ?? ''}`;

      // 4. 해당 키에 데이터 매핑
      acc[compositeKey][bindingKey] = curr.eqpCnt;
      acc[compositeKey][bindingKey + '|cntHdfgInsYn'] = curr.cntHdfgInsYn;

      return acc;
    }, {} as Record<string, MdIvesMdlEqpLnIspt>);

    // 5. 객체를 배열로 변환하여 반환
    return Object.values(groupedObj);
  };

  // pivot -> list 변경 함수
  const transFormGridForData = (pivot): MdIvesMdlEqpLnIspt[] => {
    const resultData: MdIvesMdlEqpLnIspt[] = [];
    const targetKey = specMappingCodes.map((o) => o.cmnCd);

    pivot.forEach((item) => {
      targetKey.forEach((key) => {
        const eqpCnt = item[key || ''];
        const cntHdfgInsYn = item[key + '|cntHdfgInsYn'];

        if (!_.isNil(eqpCnt)) {
          resultData.push({
            elmPrjId,
            ivesMdlId,
            prdnLnId: item.prdnLnId,
            prdnLnTpCd: item.prdnLnTpCd,
            eqpCnt,
            optValCtn1: key,
            cntHdfgInsYn,
          });
        }
      });
    });
    return resultData;
  };

  const initCodes = async () => {
    const specMapping = await getCommonCodeNamesCondition({
      cmnGrCd: 'MD_EQCL_SPEC_MAPPING',
      optValCtn4: 'Y',
    });
    setSpecMappingCodes(specMapping);
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (CellType.Cell === e.panel.cellType) {
        const col = s.columns[e.col];
        const item = s.rows[e.row].dataItem;

        if (col.binding && item) {
          const flagKey = `${col.binding}|cntHdfgInsYn`;

          if (item[flagKey] === 'Y' && isEditable) {
            // 수기 입력된 셀의 스타일 지정
            e.cell.style.backgroundColor = '#e3f0ff';
            e.cell.style.color = '#3b5db7';
            e.cell.style.fontWeight = 'bold';
            e.cell.style.border = '1px solid #3b5db7';
          } else {
            e.cell.style.backgroundColor = '';
            e.cell.style.color = '';
            e.cell.style.fontWeight = '';
            e.cell.style.border = '';
          }
        }

        if (e.col >= 3 && isEditable) {
          e.cell.ariaReadOnly = false;
        } else {
          e.cell.ariaReadOnly = true;
        }
      }
    });
  };

  const onBeginningEdit = (s, e) => {
    if (e.col >= 3 && isEditable) {
      e.cancel = false;
    } else {
      e.cancel = true;
    }
    // 편집하기 전 원본 데이터 저장
    originalValueRef.current = s.getCellData(e.row, e.col, false);
  };

  const onCellEditEnded = (s, e) => {
    // 방금 편집을 마친 후의 새로운 값
    const newValue = s.getCellData(e.row, e.col, false);

    // 💡 4. 원본 값과 새로운 값이 '실제로 다를 때만' 플래그를 업데이트합니다.
    if (originalValueRef.current !== newValue) {
      const col = s.columns[e.col];
      const bindingKey = col.binding;
      const item = s.rows[e.row].dataItem;

      if (bindingKey && item) {
        const flagKey = `${bindingKey}|cntHdfgInsYn`;
        item[flagKey] = 'Y';
        s.invalidate(); // 색상 변경을 위해 그리드 다시 그리기
      }
    }

    // 검사가 끝났으므로 ref 초기화
    originalValueRef.current = null;
  };

  const onChangeTab = async (e, v) => {
    const data = gridRef.current.rows.map((o) => o.dataItem);
    const reqData = transFormGridForData(data);
    await saveMdIvesLnEqpIsptList(reqData);
    setCurrentTabIndex(v);
  };

  const btnReset = () => {
    const checkedIds: string[] = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem)
      ?.map((o) => o.prdnLnId);

    if (checkedIds.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    gridRef.current.rows.forEach((row, idx) => {
      if (checkedIds.includes(row.dataItem.prdnLnId)) {
        row.dataItem = {
          ...rollbackData.current.find((o) => o.prdnLnId === row.dataItem.prdnLnId),
        };
        row.isSelected = false;
        gridRef.current.invalidate();
      }
    });
  };

  const btnSave = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: async () => {
        const data = gridRef.current.rows.map((o) => o.dataItem);
        const reqData = transFormGridForData(data);
        openLoading(true);
        // save
        saveMdIvesLnEqpIsptList(reqData)
          .then(async (res) => {
            openMessageBar({
              type: 'confirm',
              content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
            });
            rollbackData.current = _.cloneDeep(reqData);
            setIsInit((prev) => !prev);
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  useEffect(() => {
    initCodes();
  }, []);
  useEffect(() => {
    getLnEqpData();
  }, [elmPrjId, ivesMdlId, currentTabIndex, isInit]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.라인/설비대수 점검', '라인/설비대수 점검')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <div css={tabs.wrap} className="mt-6">
        <Tabs value={currentTabIndex} onChange={onChangeTab} css={tabs.tab}>
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`td-ives-cmpu-tab-${idx}`}
              aria-controls={`td-ives-cmpu-tabpanel-${idx}`}
            />
          ))}
          <SubTitleGroup className="mt-1 ml-1">
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </Tabs>
        {isEditable && (
          <div css={tabs.btn} className="btn-md">
            <Button
              css={IconButton.button}
              className="icoNone"
              disabled={!isModify() || !isRegStat}
              onClick={btnReset}
            >
              {t('com.label.초기화', '초기화')}
            </Button>
            <Button
              css={IconButton.button}
              className="save"
              disabled={!isModify() || !isRegStat || isLoading}
              onClick={btnSave}
            >
              {t('com.label.저장', '저장')}
            </Button>
          </div>
        )}
      </div>
      <CustomGrid
        key={`grid${isEditable}`}
        id={`line-eqp-nbu-ispt-grid0`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnded={onCellEditEnded}
        isSelector={isEditable}
        isReadOnly={!isEditable}
        height={200}
      />
      {isOpenAddPopup && (
        <LineInqPopup
          open={isOpenAddPopup}
          close={() => setIsOpenAddPopup(false)}
          callbackFn={(items) => console.log(items)}
          ivesMdlId={ivesMdlId}
          elmPrjId={elmPrjId}
          prdnLnTpCds={tabMenus?.map((o) => o.id || '')}
          selectedItems={rowData}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-tabpanel-${index}`}>
      <div>{children}</div>
    </div>
  );
}

export default memo(LineEqpNbuIspt);
